import { useEffect, useRef, useState } from 'react';
import { useTooltip } from '@/hooks/useTooltip';
import { Icon } from '@/components/Icon';

/**
 * Единичный выбор в общем визуальном языке формы (кругло, подсветка синим
 * при наведении/выборе) — заменяет нативный <select> с его нестилизуемым
 * системным списком (Тип/Тип тарификации/Статус/Шаблон соглашения и т.п.).
 */
export function CustomSelect({
  value,
  options,
  onChange,
  disabled = false,
  tooltip,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  disabled?: boolean;
  tooltip?: string;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const selectedLabel = options.find((o) => o.value === value)?.label;
  const tooltipHook = useTooltip<HTMLButtonElement>(tooltip);

  return (
    <div className="multiselect" ref={containerRef}>
      <button
        ref={tooltipHook.ref}
        type="button"
        className="multiselect__control"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        {...tooltipHook.handlers}
      >
        {selectedLabel ? <span>{selectedLabel}</span> : <span className="multiselect__placeholder">Не выбрано</span>}
        <Icon name="chevronDown" size={12} className="multiselect__arrow" />
        {tooltipHook.node}
      </button>

      {open && !disabled && (
        <div className="multiselect__dropdown">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              className={`multiselect__option${o.value === value ? ' multiselect__option--active' : ''}`}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
